import type { EventSource } from "@/types/discover";
import { hasTimeComponent } from "@/utils/date";
import type { TranslationValues } from "use-intl/core";

export function isEventEnded(
  event: {
    end_date?: string;
    start_date: string;
    end_time?: string | null;
  },
  now: Date = new Date(),
): boolean {
  if (event.end_time && hasTimeComponent(event.end_time)) {
    return new Date(event.end_time).getTime() < now.getTime();
  }

  const raw = event.end_date || event.start_date;
  // Ingested values are date-only ("2026-07-10", parsed as UTC midnight); the
  // event ends at the END of that day (UTC, matching ingestion's anchoring),
  // not at its first instant. Values with a time component keep their exact
  // instant.
  const endMs = hasTimeComponent(raw)
    ? new Date(raw).getTime()
    : new Date(raw).getTime() + 24 * 60 * 60 * 1000;
  return endMs < now.getTime();
}

export type CtaState = {
  label: string;
  disabled: boolean;
  variant: "default" | "secondary" | "outline" | "ghost" | "destructive";
};

export function isRegistrationDeadlinePassed(
  event: { registration_deadline?: string | null },
  now: Date = new Date(),
): boolean {
  if (!event.registration_deadline) return false;

  return new Date(event.registration_deadline).getTime() < now.getTime();
}

/**
 * Whether a public (group-backed) event is open on the calendar: not ended, not
 * archived or cancelled, and inside its registration deadline. This decides
 * the CTA's label and variant; it is not on its own whether the button is
 * enabled, because capacity is a separate gate.
 *
 * `registration_opens` is deliberately ignored: for public events ingestion
 * (discover-data-ingestion/src/transformers/public_events.py) sets it to the
 * event's `start_time`, so it is not a real "registration opens" window. Gating
 * on it would read "Registration opens in N days" on essentially every upcoming
 * public event, until the event itself started.
 */
export function isPublicEventJoinable(
  event: EventSource,
  now: Date = new Date(),
): boolean {
  if (isEventEnded(event, now)) return false;
  if (event.archived || event.cancelled) return false;
  if (isRegistrationDeadlinePassed(event, now)) return false;
  return true;
}

function withdrawnLabel(
  event: EventSource,
  t: (key: string) => string,
): string | null {
  if (event.cancelled) return t("signup.eventCancelled");
  if (event.archived) return t("signup.eventArchived");
  return null;
}

export function computeEventCtaState(
  event: EventSource,
  t: (key: string, values?: TranslationValues) => string,
  now: Date = new Date(),
): CtaState {
  const registrationOpensDate = event.registration_opens
    ? new Date(event.registration_opens)
    : null;
  const hasEventEnded = isEventEnded(event, now);
  const hasRegistrationDeadlinePassed = isRegistrationDeadlinePassed(
    event,
    now,
  );
  const opensInDays =
    registrationOpensDate && registrationOpensDate.getTime() > now.getTime()
      ? Math.ceil(
          (registrationOpensDate.getTime() - now.getTime()) /
            (1000 * 60 * 60 * 24),
        )
      : 0;
  const shouldShowOpensIn = !hasEventEnded && opensInDays > 0;
  const hasRemainingSeats = event.remaining === null || event.remaining > 0;
  const withdrawn = withdrawnLabel(event, t);
  const disabled =
    hasEventEnded ||
    withdrawn !== null ||
    hasRegistrationDeadlinePassed ||
    shouldShowOpensIn ||
    !hasRemainingSeats;
  const variant = disabled ? "secondary" : "default";

  let label = "";
  if (hasEventEnded) {
    label = t("signup.eventEnded");
  } else if (withdrawn !== null) {
    label = withdrawn;
  } else if (hasRegistrationDeadlinePassed) {
    label = t("signup.registrationClosed");
  } else if (shouldShowOpensIn) {
    label = `${t("signup.registrationOpensIn")} ${opensInDays} ${opensInDays === 1 ? t("common.day") : t("common.days")}`;
  } else if (!hasRemainingSeats) {
    label = t("signup.fullyBooked");
  } else {
    label = t("signup.cta");
  }

  return { label, disabled, variant };
}
